import React, { useEffect, useState } from 'react'
import { Dialog } from 'primereact/dialog'
import DynamicDataTable from '../../common/components/Common/DataListing'
import { getSessions, createSession, updateSession, deleteSession } from '../../api/sessionApi'
import SessionForm from './SessionForm'
import { toast } from 'react-toastify'
import { InputSwitch } from 'primereact/inputswitch'
import { TabView, TabPanel } from 'primereact/tabview'
import { useSchoolFeatures } from '../../common/utils/schoolSettings'
import TermsManager from './TermsManager'

const SessionListing = () => {
  const { termsEnabled } = useSchoolFeatures()
  const [sessions, setSessions] = useState([])
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [editing, setEditing] = useState(false)
  const [current, setCurrent] = useState(null)

  const fetchSessions = async () => {
    try {
      const data = await getSessions()
      setSessions(data || [])
    } catch (err) {
      toast.error('Failed to load sessions')
    }
  }

  useEffect(() => {
    fetchSessions()
  }, [])

  const handleCreate = () => {
    setEditing(false)
    setCurrent(null)
    setIsModalVisible(true)
  }

  const handleEdit = (row) => {
    setEditing(true)
    setCurrent(row)
    setIsModalVisible(true)
  }

  const handleDelete = async (rowId) => {
    try {
      await deleteSession(rowId)
      toast.success('Session deleted')
      await fetchSessions()
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to delete session')
    }
  }

  const handleToggleActive = async (row, nextValue) => {
    // If trying to activate and another session is already active, block and show message
    if (nextValue) {
      const anotherActive = sessions.find(s => !!s.is_active && s.id !== row.id)
      if (anotherActive) {
        toast.info('Please deactivate the currently active session before activating another session.')
        return
      }
    }

    try {
      await updateSession(row.id, {
        name: row.name,
        start_date: row.start_date,
        end_date: row.end_date,
        is_active: nextValue
      })
      // toast.success(nextValue ? 'Session enabled' : '')
      await fetchSessions()
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to update session status')
    }
  }

  const handleSubmit = async (formValues) => {
    setIsSubmitting(true)
    try {
      if (editing && current) {
        await updateSession(current.id, formValues)
        toast.success('Session updated')
      } else {
        await createSession(formValues)
        toast.success('Session created')
      }
      setIsModalVisible(false)
      await fetchSessions()
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to save session')
    } finally {
      setIsSubmitting(false)
    }
  }

  const columns = [
    { field: 'name', header: 'Name' },
    { 
      header: 'Start Date',
      body: (row) => row.start_date ? new Date(row.start_date).toLocaleDateString() : ''
    },
    { 
      header: 'End Date',
      body: (row) => row.end_date ? new Date(row.end_date).toLocaleDateString() : ''
    },
    { 
      header: 'Active',
      body: (row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <InputSwitch
            checked={!!row.is_active}
            onChange={(e) => handleToggleActive(row, e.value)}
          />
        </div>
      )
    }
  ]

  const sessionTable = (
    <DynamicDataTable
      data={sessions}
      subject={'Session'}
      columns={columns}
      onCreate={handleCreate}
      onEdit={handleEdit}
      onDelete={handleDelete}
    />
  )

  return (
    <div>
      {/* Terms are opt-in per school; without them this page is unchanged. */}
      {termsEnabled ? (
        <TabView>
          <TabPanel header="Sessions" leftIcon="pi pi-calendar mr-2">
            {sessionTable}
          </TabPanel>
          <TabPanel header="Terms" leftIcon="pi pi-clock mr-2">
            <TermsManager />
          </TabPanel>
        </TabView>
      ) : (
        sessionTable
      )}

      <Dialog
        header={editing ? 'Edit Session' : 'Add New Session'}
        visible={isModalVisible}
        onHide={() => setIsModalVisible(false)}
        style={{ width: '40rem' }}
        modal
        blockScroll
      >
        <SessionForm onSubmit={handleSubmit} submitting={isSubmitting} initialValues={current} isEdit={editing} />
      </Dialog>
    </div>
  )
}

export default SessionListing
